import type { AxiosError } from 'axios';

import i18n from '../i18n';

export interface ErrorDataObject {
  details: string;
  status?: number;
  title?: string;
  type?: string;
}

const TECHNICAL_ERROR_CODE_PATTERN = /^[a-z][a-z0-9]*(?:[._:-]?[a-z0-9]+)*$/;
const TECHNICAL_ERROR_PREFIX_PATTERN =
  /^[a-z][a-z0-9]*(?:[._:-]?[a-z0-9]+)*\s*:/;

const isTechnicalErrorText = (value: string): boolean => {
  const trimmed = value.trim();

  return (
    TECHNICAL_ERROR_CODE_PATTERN.test(trimmed) ||
    TECHNICAL_ERROR_PREFIX_PATTERN.test(trimmed)
  );
};

const normalizeErrorText = (
  value: unknown,
  fallback: string
): string => {
  if (typeof value !== 'string') return '';

  const trimmed = value.trim();
  if (!trimmed) return '';

  const translationKey = `common:errors.${trimmed}`;
  if (i18n.exists(translationKey)) return i18n.t(translationKey);

  return isTechnicalErrorText(trimmed) ? fallback : trimmed;
};

const extractErrorText = (value: unknown): unknown => {
  if (typeof value === 'string') return value;
  if (typeof value !== 'object' || value === null) return value;

  const data = value as Record<string, unknown>;
  if (typeof data.message === 'string') return data.message;

  const issues = data.issues;
  if (Array.isArray(issues)) {
    const issue = issues.find(
      (item): item is { message: string } =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as { message?: unknown }).message === 'string'
    );
    if (issue) return issue.message;
  }

  return value;
};

const normalizeErrorDetails = (value: unknown): string => {
  const fallback = i18n.t('common:errors.generic');
  const text = extractErrorText(value);
  const normalized = normalizeErrorText(text, fallback);

  return normalized === fallback &&
    typeof text === 'string' &&
    isTechnicalErrorText(text)
    ? ''
    : normalized;
};

const normalizeErrorTitle = (value: unknown): string => {
  return normalizeErrorText(value, i18n.t('common:errors.generic'));
};

export class ApiError extends Error {
  public status?: number;

  public data?: ErrorDataObject;

  public url?: string;

  public originalError: unknown;

  constructor(
    message: string,
    status?: number,
    data?: ErrorDataObject,
    url?: string,
    originalError?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.url = url;
    this.originalError = originalError;
  }

  static fromError(error: unknown): ApiError {
    if (error instanceof ApiError) {
      return error;
    }

    // Axios error
    if (
      typeof error === 'object' &&
      error !== null &&
      'isAxiosError' in error &&
      error.isAxiosError
    ) {
      const axiosError = error as AxiosError;

      let data: ErrorDataObject | undefined;
      let title = i18n.t('common:errors.generic');

      if (axiosError.response?.data) {
        if (typeof axiosError.response.data === 'string') {
          data = {
            details: normalizeErrorDetails(axiosError.response.data),
          };
        } else if (typeof axiosError.response.data === 'object') {
          const responseData = axiosError.response.data as Record<
            string,
            unknown
          >;
          const normalizedTitle = normalizeErrorTitle(responseData.title);
          const assignDetails = (value: unknown) => {
            const details = normalizeErrorDetails(value);
            if (!data || !data.details) {
              data = {
                ...responseData,
                details,
              };
            }
          };

          if (normalizedTitle) {
            title = normalizedTitle;
          }

          if ('detail' in responseData) {
            assignDetails(responseData.detail);
          }
          if ('details' in responseData) {
            assignDetails(responseData.details);
          }
          if ('msg' in responseData) {
            assignDetails(responseData.msg);
          }
          // Hono's default onError handler returns { error: <message>, code: <status> }.
          // Surface that as `details` so callers can show a meaningful message
          // instead of the generic 'Произошла ошибка' i18n fallback.
          if ('error' in responseData) {
            assignDetails(responseData.error);
          }
          if ('message' in responseData) {
            assignDetails(responseData.message);
          }
          if ('code' in responseData) {
            assignDetails(responseData.code);
          }

          if (data && normalizedTitle) data.title = normalizedTitle;
        }
      }

      return new ApiError(
        title,
        axiosError.response?.status || axiosError.status,
        data,
        axiosError.config?.url,
        error
      );
    }

    // Native error
    if (error instanceof Error) {
      return new ApiError(error.message, 0, undefined, undefined, error);
    }

    // Unknown non-error
    return new ApiError(
      i18n.t('common:errors.generic'),
      0,
      undefined,
      undefined,
      error
    );
  }

  static isCanceledError(error: unknown): boolean {
    if (!error) return false;

    const check = (value?: unknown) => {
      if (typeof value !== 'string') return false;
      const normalized = value.toLowerCase();
      return (
        normalized.includes('cancellederror') ||
        normalized.includes('cancelederror') ||
        normalized.includes('query was cancelled') ||
        normalized.includes('query was canceled') ||
        normalized === 'canceled' ||
        normalized === 'cancelled'
      );
    };

    if (error instanceof ApiError) {
      return (
        check(error.message) ||
        ApiError.isCanceledError(error.originalError)
      );
    }

    if (error instanceof Error) {
      const anyError = error as Error & { code?: string; __CANCEL__?: boolean };
      return (
        check(anyError.name) ||
        check(anyError.message) ||
        anyError.code === 'ERR_CANCELED' ||
        anyError.__CANCEL__ === true
      );
    }

    if (typeof error === 'object' && error !== null) {
      const maybeError = error as {
        name?: unknown;
        message?: unknown;
        code?: unknown;
        __CANCEL__?: unknown;
      };
      return (
        check(maybeError.name) ||
        check(maybeError.message) ||
        maybeError.code === 'ERR_CANCELED' ||
        maybeError.__CANCEL__ === true
      );
    }

    return false;
  }
}
